import type { InfiniteData } from "@tanstack/react-query";
import type { PaginatedResponse, ProjectRecord } from "@/types/api";

/** One filter combination's record list — `recordKeys.list`. */
export type RecordListCache = InfiniteData<PaginatedResponse<ProjectRecord>>;

/**
 * Anything cached under `recordKeys.byProject`: the lists, and the single
 * records read by `recordKeys.detail` (the issue page's evidence, a photo
 * opened from a report). A writer that patches "every record cache in the
 * project" meets both shapes.
 */
export type RecordCacheEntry = RecordListCache | ProjectRecord;

export function isRecordListCache(data: unknown): data is RecordListCache {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

/**
 * Apply `update` to the record `recordId` wherever this cache entry holds it —
 * on every page of a list, or as the entry itself. Anything else is returned
 * untouched.
 */
export function updateCachedRecord(
  data: RecordCacheEntry | undefined,
  recordId: string,
  update: (record: ProjectRecord) => ProjectRecord,
): RecordCacheEntry | undefined {
  if (!data) return data;
  if (isRecordListCache(data)) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        results: page.results.map((record) =>
          record.id === recordId ? update(record) : record,
        ),
      })),
    };
  }
  return data.id === recordId ? update(data) : data;
}
